import { useContext } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { TrashSimple } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { withErrorBoundary } from "@/hoc/with-error-boundary"
import { toast } from "@/hooks/useToast.ts"
import { formatDate } from "@/lib/format-date"
import { LastModificationInfo } from "@/modules/patients/detail/last-modification-info"
import { useMedications } from "@/modules/patients/detail/medication/hooks/useMedications"
import { useLatestDiagnosisCode } from "@/modules/patients/detail/progress-note/hooks/useLatestDiagnosisCode.ts"
import { ProgressNoteFormContext } from "@/modules/patients/detail/progress-note/hooks/useProgressNoteFormStore"
import ReferralForm from "@/modules/patients/detail/referral/ch/ch-referral-form"
import { useChCreateReferralMutation$data } from "@/modules/patients/detail/referral/ch/services/__generated__/useChCreateReferralMutation.graphql"
import { useChUpdateReferralMutation$data } from "@/modules/patients/detail/referral/ch/services/__generated__/useChUpdateReferralMutation.graphql"
import { chReferralLetterFragment } from "@/modules/patients/detail/referral/ch/services/chReferralLetterFragment.ts"
import { useChCreateReferralMutation } from "@/modules/patients/detail/referral/ch/services/useChCreateReferralMutation.ts"
import { useChDeleteReferralMutation } from "@/modules/patients/detail/referral/ch/services/useChDeleteReferralMutation.ts"
import { useChGenerateReferralDocumentMutation } from "@/modules/patients/detail/referral/ch/services/useChGenerateReferralDocumentMutation"
import { useChUpdateReferralMutation } from "@/modules/patients/detail/referral/ch/services/useChUpdateReferralMutation.ts"
import { ChReferralSchema, chReferralSchema } from "@/modules/patients/detail/referral/schema/referral-schema.ts"
import { useReferralStoreContext } from "@/modules/patients/detail/referral/store/useReferralStore"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam.ts"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam.ts"
import { getDefaultDocumentLanguageValue } from "@/modules/patients/libs/document-languages.ts"
import { PatientAllergiesOutputFragment$key } from "@/modules/patients/services/__generated__/PatientAllergiesOutputFragment.graphql"
import { PatientConditionInformationOutputFragment$key } from "@/modules/patients/services/__generated__/PatientConditionInformationOutputFragment.graphql"
import { PatientAllergiesOutputFragment } from "@/modules/patients/services/PatientAllergiesOutputFragment"
import { PatientConditionInformationOutputFragment } from "@/modules/patients/services/PatientConditionInformationOutputFragment"
import { usePatientByIdQuery } from "@/modules/patients/services/usePatientByIdQuery"
import { useConfirmStore } from "@/stores/useConfirmStore.ts"
import { useDrawerStore } from "@/stores/useDrawerStore.tsx"

type ReferralLetter =
  | useChCreateReferralMutation$data["ch_createReferralLetter"]["ch_ReferralLetter"]
  | useChUpdateReferralMutation$data["ch_updateReferralLetter"]["ch_ReferralLetter"]

const ChReferralDrawer = withErrorBoundary((props: FetchKeyProps) => {
  const progressNoteFormStore = useContext(ProgressNoteFormContext)
  if (!progressNoteFormStore) throw new Error("Missing ProgressNoteFormProvider in the tree")

  const { t } = useTranslation()
  const patientId = usePatientParam()
  const countryCode = useCountryParam()
  const appointmentId = useAppointmentParam()

  const [patientData] = usePatientByIdQuery(patientId, countryCode, appointmentId)
  const { activeMedications, inactiveMedications } = useMedications()

  const medicationList = [...activeMedications, ...inactiveMedications]

  const { conditionInformation: conditionInformationRef, allergiesInformation: allergiesInformationRef } =
    patientData.patientById!

  const { conditions: conditionList } = useFragment<PatientConditionInformationOutputFragment$key>(
    PatientConditionInformationOutputFragment,
    conditionInformationRef,
  )

  const { allergies: allergyList } = useFragment<PatientAllergiesOutputFragment$key>(
    PatientAllergiesOutputFragment,
    allergiesInformationRef,
  )

  const progressNoteId = useReferralStoreContext((state) => state.progressNoteId)
  const setProgressNoteId = useReferralStoreContext((state) => state.setProgressNoteId)
  const careProvider = useReferralStoreContext((state) => state.careProvider)
  const referralLetterRef = useReferralStoreContext((state) => state.referralLetterRef)
  const setReferralLetterRef = useReferralStoreContext((state) => state.setReferralLetterRef)
  const progressNoteCreatedDate = useReferralStoreContext((state) => state.progressNoteCreatedDate)
  const referralData = useFragment(chReferralLetterFragment, referralLetterRef)
  const [generateChReferralDocument, isGenerating] = useChGenerateReferralDocumentMutation()

  const { diagnosisCode: latestDiagnosisCode } = useLatestDiagnosisCode(progressNoteId || undefined)
  const isReferralFormEditMode = !!referralData?.id
  const updatedAt = referralData?.updatedAt || referralData?.createdAt
  const updatedBy = referralData?.updatedBy || referralData?.createdBy

  const documentData = referralData
    ? {
        diagnosisCode: referralData?.icdDiagnosis.diagnosisCode,
        reasoning: referralData.reasoning || "",
        documentLanguage:
          "documentLanguage" in referralData
            ? getDefaultDocumentLanguageValue(referralData.documentLanguage)
            : undefined,
        careProviderCopyToIds: "careProviderIds" in referralData ? (referralData.careProviderIds ?? []) : [],
        allergies: referralData?.allergies || [],
        conditions: referralData?.conditions || [],
        medicationIds: referralData?.medicationIds || [],
      }
    : null
  const hasInvalidCareProvider = countryCode === "CH" && careProvider === null
  const form = useForm<ChReferralSchema>({
    resolver: zodResolver(chReferralSchema),
    defaultValues: documentData ?? {
      diagnosisCode: latestDiagnosisCode,
      reasoning: "",
      documentLanguage: "GERMAN",
      careProviderCopyToIds: [],
    },
  })

  const {
    formState: { isDirty },
  } = form
  const [createReferral, isCreating] = useChCreateReferralMutation()
  const [updateReferral, isUpdating] = useChUpdateReferralMutation()
  const [deleteReferral, isDeleting] = useChDeleteReferralMutation()

  const closeDrawer = useDrawerStore((state) => state.closeDrawer)
  const confirm = useConfirmStore((state) => state.confirm)
  const cancel = useConfirmStore((state) => state.cancel)

  const handleCloseDrawer = () => {
    setProgressNoteId("")
    closeDrawer()
    setReferralLetterRef(null)
  }

  const onSubmit = (data: ChReferralSchema) => {
    if (!progressNoteId) return

    if (isReferralFormEditMode) {
      updateReferral({
        variables: {
          input: {
            reasoning: data.reasoning,
            documentLanguage: data.documentLanguage,
            id: referralData.id,
            careProviderCopyToIds: data.careProviderCopyToIds || [],
            allergies: data?.allergies || [],
            conditions: data?.conditions || [],
            medicationIds: data?.medicationIds || [],
            diagnosisCode: data.diagnosisCode,
          },
        },
        onCompleted: (response) => {
          const referral = response.ch_updateReferralLetter.ch_ReferralLetter
          const id = referral?.id
          if (id) {
            generateDocument(id, data, referral)
          }
        },
      })
    } else {
      createReferral({
        variables: {
          input: {
            reasoning: data.reasoning,
            documentLanguage: data.documentLanguage,
            patientId: patientId,
            progressNoteId: progressNoteId,
            careProviderCopyToIds: data.careProviderCopyToIds || [],
            allergies: data?.allergies || [],
            conditions: data?.conditions || [],
            medicationIds: data?.medicationIds || [],
            diagnosisCode: data.diagnosisCode,
          },
        },
        onCompleted: (response) => {
          const referral = response.ch_createReferralLetter.ch_ReferralLetter
          const id = referral?.id

          const episodeOfCareProgressNoteData = progressNoteFormStore.getState().progressNoteForms.get(progressNoteId)

          if (episodeOfCareProgressNoteData) {
            progressNoteFormStore
              .getState()
              .setProgressNoteForm(progressNoteId, episodeOfCareProgressNoteData.episodeOfCareProgressNoteData, {
                kind: "BetterDocMSNReferralLetter",
                data: {
                  hasErrors: false,
                },
              })
          }

          if (id) {
            generateDocument(id, data, referral)
          }
        },
        updater(store, data) {
          try {
            const progressNoteRecordProxy = store.get(progressNoteId)
            const referralLetter = data?.ch_createReferralLetter?.ch_ReferralLetter

            if (referralLetter?.id) {
              const newRecordProxy = store.get(referralLetter.id)
              if (newRecordProxy) {
                progressNoteRecordProxy?.setLinkedRecord(newRecordProxy, "ch_referralLetter")
              }
            }
          } catch (error) {
            console.error("Error while processing data:", error)
          }
        },
      })
    }
  }

  const handleDelete = () => {
    if (!progressNoteId || !referralData) return

    confirm({
      confirmText: "common.button.delete",
      cancelText: "common.button.keep",
      title: `${t("common.button.delete")} ${t("episodeOfCare.progressNote.referralLetter.default")}`,
      content: "episodeOfCare.deleteProgressNote.content",
      onConfirm: () =>
        deleteReferral({
          variables: {
            input: {
              id: referralData.id,
            },
          },
          onCompleted: () => {
            cancel()
            handleCloseDrawer()
            toast({
              description: t("patient.referralLetter.deleteMessage.success"),
            })
          },
        }),
    })
  }

  const generateDocument = (id: string, formData: ChReferralSchema, response: ReferralLetter) => {
    if (!progressNoteId) return

    const { allergies = [], medicationIds = [], conditions = [], reasoning = "" } = formData

    const episodeOfCare = response?.icdDiagnosis.text ?? ""

    const formattedAllergies = allergies.map((id) => {
      return allergyList.find((item) => item.icdDiagnosis.id === id)?.icdDiagnosis.text ?? ""
    })
    const formattedConditions = conditions.map((id) => {
      return conditionList.find((item) => item.icdDiagnosis.id === id)?.icdDiagnosis.text ?? ""
    })
    const formattedMedications = medicationIds.map((id) => {
      return medicationList.find((item) => item.id === id)?.name ?? ""
    })

    generateChReferralDocument({
      params: {
        id,
        allergies: formattedAllergies?.join(", ") || "-",
        conditions: formattedConditions?.join(", ") || "-",
        medications: formattedMedications?.join(", ") || "-",
        reasoning,
        episodeOfCare,
        dateOfConsultation: formatDate(progressNoteCreatedDate),
        keepOriginalAuthor: false,
      },
      onCompleted() {
        handleCloseDrawer()
      },
      onError() {
        handleCloseDrawer()
      },
    })
  }

  return (
    <Drawer open onOpenChange={handleCloseDrawer}>
      <FormProvider {...form}>
        <DrawerContent>
          <DrawerClose className="top-2 right-2" />
          <DrawerHeader className="mb-4 pr-4" data-testid="referral-header">
            <DrawerTitle className="animate-fade-in-bottom text-on-surface-subdued text-sm font-medium">
              {t("episodeOfCare.progressNote.referral")}
            </DrawerTitle>
          </DrawerHeader>
          <DrawerBody className="flex gap-4 pt-0.5" data-testid="referral-body">
            <div className="grid gap-8">
              {!!progressNoteId && <ReferralForm fetchKey={props.fetchKey} progressNoteId={progressNoteId} />}
            </div>
          </DrawerBody>
          <DrawerFooter className="flex-wrap gap-2" data-testid="referral-footer">
            {referralData && (
              <div className="flex gap-2">
                {updatedAt && updatedBy && <LastModificationInfo updatedAt={updatedAt} updatedBy={updatedBy} />}
                <button onClick={handleDelete} data-testid="ch-sick-leave-delete-button">
                  <TrashSimple className="text-on-surface-subdued" />
                </button>
              </div>
            )}
            <div className="ml-auto flex gap-2">
              <Button variant="secondary" data-testid="referral-cancel-button" onClick={handleCloseDrawer}>
                {t("common.button.cancel")}
              </Button>
              <Button
                disabled={isReferralFormEditMode && !isDirty && hasInvalidCareProvider}
                onClick={() => {
                  FaroInsightsWrapper.pushEvent("documents_referral_save")
                  form.handleSubmit(onSubmit)()
                }}
                data-testid="referral-save-button"
                isLoading={isCreating || isUpdating || isDeleting || isGenerating}>
                {t("common.button.save")}
              </Button>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </FormProvider>
    </Drawer>
  )
}, "ChReferralDrawer")

export { ChReferralDrawer }
